import { useState } from "react";
import { Modal } from "@/ui/Modal";
import { Calout } from "@/ui/Calout";
import { deletePage } from "@/api/pages";
import { getSessionToken } from "@/api/auth";

// AMB-701 — delete an HTML page. A published page is taken offline first (the server
// refuses the delete if it can't), so a deleted page never stays live.
export const DeleteHtmlPageModal = ({
  page,
  siteAddress,
  setIsOpen,
  onDeleted,
}: {
  page: { id: string; name?: string; slug?: string; pageSettings?: any };
  siteAddress?: string;
  setIsOpen: (isOpen: boolean) => void;
  onDeleted: () => void;
}) => {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const liveSlug = page?.pageSettings?.html?.publishedSlug;

  const handleDelete = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    const response = await deletePage(page.id, { token: getSessionToken() });
    setBusy(false);
    if (!response || response.error) return setError(response?.error || "The page couldn't be deleted. Try again.");
    setIsOpen(false);
    onDeleted();
  };

  return (
    <Modal
      title={`Delete "${page?.name || "this page"}"?`}
      onClose={() => setIsOpen(false)}
      onSuccess={handleDelete}
      successCaption={busy ? "Deleting…" : liveSlug ? "Delete and take offline" : "Delete"}
    >
      <div className="flex flex-col gap-3 text-left">
        <div className="text-sm text-black/70 dark:text-white/70">
          {liveSlug ? (
            <>
              This page is live at{" "}
              <span className="font-medium text-black dark:text-white break-all">
                {siteAddress ? `${siteAddress}/` : "/"}
                {liveSlug}
              </span>
              . Deleting it removes it from this site <b>and takes it offline</b>. Visitors to that address will see the
              site's not-found page.
            </>
          ) : (
            "This draft was never published, so nothing live changes."
          )}
        </div>
        <Calout title="There's no undo. Keep a copy of the page file if you might need it again." />
        {error && <Calout title={error} />}
      </div>
    </Modal>
  );
};
